import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { MuiTelInput } from 'mui-tel-input';
import { useTranslation } from 'react-i18next';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { useSignup } from '../SignupContext';
import { getMarketplaceAuth } from '../../../firebase';
import {
  signInWithGoogle,
  signOutMarketplace,
} from '../../../services/MarketplaceAuthService';

const fieldSx = {
  '& .MuiInputBase-root': {
    fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
    color: 'var(--ink)',
    '&::before': { borderBottom: '1px solid var(--dust)' },
    '&:hover::before': { borderBottom: '1px solid var(--ink) !important' },
    '&::after': { borderBottom: '2px solid var(--sun)' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: '"JetBrains Mono Variable", monospace',
    fontSize: '0.75rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--ink-soft)',
    '&.Mui-focused': { color: 'var(--sun-deep)' },
  },
  '& .MuiInput-input': { padding: '8px 0', fontSize: '1.05rem' },
  '& .MuiFormHelperText-root': {
    fontFamily: '"JetBrains Mono Variable", monospace',
    fontSize: '0.7rem',
    letterSpacing: '0.04em',
    color: 'var(--ink-soft)',
    mt: 1,
  },
};

/**
 * Rileva il paese di default dal browser locale (es. 'it-IT' → 'IT').
 * Usato per pre-impostare il prefisso internazionale del telefono.
 */
function detectDefaultPhoneCountry(): string {
  try {
    const lang = navigator.language || 'it-IT';
    const region = lang.split('-')[1] || 'IT';
    return region.toUpperCase();
  } catch {
    return 'IT';
  }
}

/**
 * Logo Google (G colorato ufficiale Material).
 * SVG inline per non richiedere asset esterni.
 */
function GoogleLogo() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 48 48"
    >
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}

export default function StepProfilo() {
  const { state, update } = useSignup();
  const { t } = useTranslation('signup');
  const [showPassword, setShowPassword] = useState(false);
  const defaultCountry = detectDefaultPhoneCountry();
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);

  useEffect(() => {
    const auth = getMarketplaceAuth();
    setAuthUser(auth.currentUser);
    const unsub = onAuthStateChanged(auth, (u) => setAuthUser(u));
    return () => unsub();
  }, []);

  const isGoogleUser = !!authUser?.providerData?.some((p) => p.providerId === 'google.com');

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setGoogleError(null);
    try {
      const result = await signInWithGoogle();
      if (result.ok) {
        update({
          admin_email: result.user.email,
          admin_nome: result.user.nome ?? '',
          admin_cognome: result.user.cognome ?? '',
        });
      } else {
        if (result.error_code !== 'auth.google.cancelled') {
          setGoogleError(result.error_code);
        }
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <Stack spacing={4}>
      <Box>
        <Typography
          variant="h2"
          sx={{
            fontFamily: '"Fraunces Variable", Georgia, serif',
            fontSize: { xs: '2.4rem', md: '3.2rem' },
            letterSpacing: '-0.02em',
            lineHeight: 1,
            mb: 2,
          }}
        >
          {t('step_profilo.titolo')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: 'var(--ink-soft)', maxWidth: 540, fontSize: '1.05rem' }}
        >
          {t('step_profilo.descrizione')}
        </Typography>
      </Box>

      {isGoogleUser ? (
        <>
          <Box
            sx={{
              padding: '20px 24px',
              border: '1px solid var(--dust)',
              borderLeft: '3px solid #34A853',
              background: 'var(--bg-deep)',
            }}
          >
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={2}
              sx={{ mb: 0.6 }}
            >
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono Variable", monospace',
                  fontSize: '0.7rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#1B5E20',
                }}
              >
                {t('step_profilo.google_attivo_titolo')}
              </Typography>
              <Box
                component="button"
                type="button"
                onClick={async () => {
                  await signOutMarketplace();
                  // Pulisci i campi auto-popolati da Google cosi' l'utente
                  // ricomincia il flusso con scelta provider libera.
                  update({
                    admin_email: undefined,
                    admin_nome: undefined,
                    admin_cognome: undefined,
                  });
                }}
                sx={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: '"JetBrains Mono Variable", monospace',
                  fontSize: '0.65rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-soft)',
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                  textDecorationColor: 'var(--dust)',
                  padding: 0,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    color: 'var(--sun-deep)',
                    textDecorationColor: 'var(--sun)',
                  },
                }}
              >
                {t('step_profilo.cambia_account')}
              </Box>
            </Stack>
            <Typography
              sx={{
                fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
                fontSize: '1.05rem',
                color: 'var(--ink)',
                mb: 0.5,
              }}
            >
              {authUser?.email}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
                fontSize: '0.9rem',
                color: 'var(--ink-soft)',
              }}
            >
              {t('step_profilo.google_attivo_descrizione')}
            </Typography>
          </Box>

          <Grid container spacing={3.5}>
            <Grid size={{ xs: 12 }}>
              <MuiTelInput
                label={t('step_profilo.telefono')}
                fullWidth
                required
                variant="standard"
                value={state.admin_telefono ?? ''}
                onChange={(value) => update({ admin_telefono: value })}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                defaultCountry={defaultCountry as any}
                preferredCountries={['IT', 'CH', 'FR', 'DE', 'ES']}
                langOfCountryName="it"
                forceCallingCode
                focusOnSelectCountry
                slotProps={{ htmlInput: { autoComplete: 'tel' } }}
                sx={{
                  ...fieldSx,
                  '& .MuiTelInput-FlagButton': {
                    marginRight: '4px',
                  },
                }}
              />
            </Grid>
          </Grid>
        </>
      ) : (
        <>
          <Stack spacing={2}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
              startIcon={
                googleLoading ? (
                  <CircularProgress
                    size={16}
                    thickness={5}
                    sx={{ color: 'inherit' }}
                  />
                ) : (
                  <GoogleLogo />
                )
              }
              sx={{
                fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
                fontSize: '0.95rem',
                fontWeight: 500,
                padding: '14px 22px',
                borderRadius: 0,
                borderColor: 'var(--ink)',
                color: 'var(--ink)',
                textTransform: 'none',
                '&:hover': {
                  background: 'var(--ink)',
                  color: 'var(--bg)',
                  borderColor: 'var(--ink)',
                },
              }}
            >
              {googleLoading
                ? t('continua_con_google.loading')
                : t('continua_con_google.button')}
            </Button>
            {googleError && (
              <Typography
                sx={{
                  color: '#B33A2E',
                  fontSize: '0.85rem',
                  fontFamily: '"JetBrains Mono Variable", monospace',
                }}
              >
                {t(`errors.${googleError}`, {
                  defaultValue: t('errors.signup.unknown_error') as string,
                })}
              </Typography>
            )}
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono Variable", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.04em',
                color: 'var(--ink-soft)',
                textAlign: 'center',
                py: 1,
              }}
            >
              {t('step_profilo.oppure_email')}
            </Typography>
          </Stack>

          <Grid container spacing={3.5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('step_profilo.nome')}
                fullWidth
                required
                variant="standard"
                value={state.admin_nome ?? ''}
                onChange={(e) => update({ admin_nome: e.target.value })}
                slotProps={{ htmlInput: { autoComplete: 'given-name' } }}
                sx={fieldSx}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t('step_profilo.cognome')}
                fullWidth
                required
                variant="standard"
                value={state.admin_cognome ?? ''}
                onChange={(e) => update({ admin_cognome: e.target.value })}
                slotProps={{ htmlInput: { autoComplete: 'family-name' } }}
                sx={fieldSx}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label={t('step_profilo.email')}
                type="email"
                fullWidth
                required
                variant="standard"
                value={state.admin_email ?? ''}
                onChange={(e) => update({ admin_email: e.target.value })}
                slotProps={{ htmlInput: { autoComplete: 'email' } }}
                sx={fieldSx}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label={t('step_profilo.password')}
                type={showPassword ? 'text' : 'password'}
                fullWidth
                required
                variant="standard"
                helperText={t('step_profilo.password_helper')}
                value={state.admin_password ?? ''}
                onChange={(e) => update({ admin_password: e.target.value })}
                slotProps={{
                  htmlInput: { autoComplete: 'new-password' },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((s) => !s)}
                          edge="end"
                          size="small"
                          aria-label={
                            showPassword
                              ? t('step_profilo.hide_password')
                              : t('step_profilo.show_password')
                          }
                          sx={{ color: 'var(--ink-soft)' }}
                        >
                          {showPassword ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={fieldSx}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <MuiTelInput
                label={t('step_profilo.telefono')}
                fullWidth
                required
                variant="standard"
                value={state.admin_telefono ?? ''}
                onChange={(value) => update({ admin_telefono: value })}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                defaultCountry={defaultCountry as any}
                preferredCountries={['IT', 'CH', 'FR', 'DE', 'ES']}
                langOfCountryName="it"
                forceCallingCode
                focusOnSelectCountry
                slotProps={{ htmlInput: { autoComplete: 'tel' } }}
                sx={{
                  ...fieldSx,
                  '& .MuiTelInput-FlagButton': {
                    marginRight: '4px',
                  },
                }}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Stack>
  );
}
